import { useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent, RefObject } from "react";

const MIN_THUMB = 36;
/** Bu kadar taşma "kaydırılabilir" sayılmaz; 1px yuvarlama çubuk çıkarmasın. */
const OVERFLOW_EPSILON = 4;
/** Son kaydırmadan bu kadar sonra çubuk söner (native overlay gibi). */
const IDLE_HIDE_MS = 1000;

interface GradientScrollbarProps {
  /** Native çubuğu gizlenmiş (`scrollbar-hide`) kaydırıcı. */
  scrollRef: RefObject<HTMLElement | null>;
  /**
   * `vertical` sağ kenarda, kaydırınca belirir.
   * `horizontal` alt kenarda durur; şeritlerde tekerlek yatay kaydırmaz,
   * çubuk görünür kalır.
   */
  orientation?: "vertical" | "horizontal";
}

/**
 * Modül renkli dikey kaydırma çubuğu.
 *
 * Native scrollbar gradyan almaz: `scrollbar-color` tek renktir, macOS overlay
 * çubuk `::-webkit-scrollbar-thumb` arka planını da yok sayar. Bu yüzden thumb
 * ayrı bir katmanda çizilir ve scroller'ın konumuna bağlanır.
 *
 * Kaydırıcının üstüne `relative` bir sarmalayıcı ister; kendisi `absolute`
 * sağ kenara oturur. Taşma yoksa DOM'da kalır ama `hidden`dır.
 *
 * Görünürlük native overlay gibi: kaydırırken belirir, 1sn durunca söner;
 * imleç sarmalayıcının üzerindeyken veya thumb sürüklenirken açık kalır.
 *
 * Çubuğun görünümü/animasyonu tek yerden değişir: bu dosya +
 * `.gradient-scrollbar-thumb` (index.css). Her kaydırıcı aynı bileşeni kullanır.
 */
export function GradientScrollbar({
  scrollRef,
  orientation = "vertical",
}: GradientScrollbarProps) {
  const horizontal = orientation === "horizontal";
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ start: number; startScroll: number } | null>(null);
  // Görünürlük kaynakları; biri açıksa çubuk görünür
  const visRef = useRef({ hover: false, drag: false, scrolling: false });

  const applyVisibility = () => {
    const track = trackRef.current;
    if (!track) return;
    // Yatay şeritte çubuk kaydırma olmadan da görünür kalır; dikey olan söner.
    if (horizontal) {
      if (!track.hidden) track.setAttribute("data-visible", "");
      return;
    }
    const v = visRef.current;
    track.toggleAttribute("data-visible", v.hover || v.drag || v.scrolling);
  };

  useEffect(() => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!scroller || !track || !thumb) return;

    let raf = 0;
    let idleTimer = 0;
    const measure = () => {
      raf = 0;
      const overflow = horizontal
        ? scroller.scrollWidth - scroller.clientWidth
        : scroller.scrollHeight - scroller.clientHeight;
      if (overflow <= OVERFLOW_EPSILON) {
        track.hidden = true;
        return;
      }
      track.hidden = false;
      if (horizontal) track.setAttribute("data-visible", "");
      const viewport = horizontal ? scroller.clientWidth : scroller.clientHeight;
      const scrollSize = horizontal ? scroller.scrollWidth : scroller.scrollHeight;
      const trackSize = horizontal ? track.clientWidth : track.clientHeight;
      const thumbSize = Math.max(MIN_THUMB, (viewport / scrollSize) * trackSize);
      const scrollPos = horizontal ? scroller.scrollLeft : scroller.scrollTop;
      const offset =
        (scrollPos / overflow) * Math.max(0, trackSize - thumbSize);
      if (horizontal) {
        thumb.style.width = `${thumbSize}px`;
        thumb.style.transform = `translateX(${offset}px)`;
      } else {
        thumb.style.height = `${thumbSize}px`;
        thumb.style.transform = `translateY(${offset}px)`;
      }
    };
    // Scroll + resize aynı karede birden çok gelebilir; tek ölçüm yeter.
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    // scrollHeight'i doğrudan gözleyen bir API yok. Kaydırıcının kendisi ve
    // doğrudan çocukları ResizeObserver'da: iç içe ne büyürse büyüsün doğrudan
    // çocuğun yüksekliği değişir. Görsel/font yüklemesi de buradan yakalanır.
    const ro = new ResizeObserver(schedule);
    const observeChildren = () => {
      ro.disconnect();
      ro.observe(scroller);
      for (const child of scroller.children) ro.observe(child);
      schedule();
    };
    observeChildren();
    // Doğrudan çocuk listesi değişince (route geçişi) yeniden abone ol.
    // `subtree` bilerek yok: streaming'de her token bir mutation olur.
    const mo = new MutationObserver(observeChildren);
    mo.observe(scroller, { childList: true });

    // Kaydırma: konumu ölç + çubuğu göster, durunca söndür
    const onScroll = () => {
      schedule();
      visRef.current.scrolling = true;
      applyVisibility();
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        visRef.current.scrolling = false;
        applyVisibility();
      }, IDLE_HIDE_MS);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });

    // Fare tekerleği yatay şeridi kaydırmaz. İmleç satırdayken dikey
    // tekerlek scrollLeft olur; uçtaysa sayfa kaydırması sürer.
    const onWheel = (e: WheelEvent) => {
      if (!horizontal) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const overflow = scroller.scrollWidth - scroller.clientWidth;
      if (overflow <= OVERFLOW_EPSILON) return;
      const atStart = scroller.scrollLeft <= 0 && e.deltaY < 0;
      const atEnd = scroller.scrollLeft >= overflow - 1 && e.deltaY > 0;
      if (atStart || atEnd) return;
      scroller.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    if (horizontal) {
      scroller.addEventListener("wheel", onWheel, { passive: false });
    }

    // Hover sarmalayıcıda dinlenir: track scroller'ın kardeşi, üstüne gelince
    // scroller'dan `pointerleave` düşerdi.
    const host = track.parentElement ?? scroller;
    const onEnter = () => {
      visRef.current.hover = true;
      applyVisibility();
    };
    const onLeave = () => {
      visRef.current.hover = false;
      applyVisibility();
    };
    host.addEventListener("pointerenter", onEnter);
    host.addEventListener("pointerleave", onLeave);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(idleTimer);
      scroller.removeEventListener("scroll", onScroll);
      scroller.removeEventListener("wheel", onWheel);
      host.removeEventListener("pointerenter", onEnter);
      host.removeEventListener("pointerleave", onLeave);
      mo.disconnect();
      ro.disconnect();
    };
  }, [scrollRef, horizontal]);

  const scrollRatio = () => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!scroller || !track || !thumb) return null;
    const overflow = horizontal
      ? scroller.scrollWidth - scroller.clientWidth
      : scroller.scrollHeight - scroller.clientHeight;
    const travel = horizontal
      ? track.clientWidth - thumb.offsetWidth
      : track.clientHeight - thumb.offsetHeight;
    if (overflow <= 0 || travel <= 0) return null;
    return { scroller, track, thumb, overflow, travel };
  };

  const onThumbPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      start: horizontal ? e.clientX : e.clientY,
      startScroll: horizontal ? scroller.scrollLeft : scroller.scrollTop,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    visRef.current.drag = true;
    applyVisibility();
  };

  const onThumbPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const m = scrollRatio();
    if (!drag || !m) return;
    const delta = (horizontal ? e.clientX : e.clientY) - drag.start;
    const next = drag.startScroll + delta * (m.overflow / m.travel);
    if (horizontal) m.scroller.scrollLeft = next;
    else m.scroller.scrollTop = next;
  };

  const onThumbPointerUp = () => {
    dragRef.current = null;
    visRef.current.drag = false;
    applyVisibility();
  };

  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || e.target !== e.currentTarget) return;
    const m = scrollRatio();
    if (!m) return;
    const rect = m.track.getBoundingClientRect();
    const thumbSize = horizontal ? m.thumb.offsetWidth : m.thumb.offsetHeight;
    const pos =
      (horizontal ? e.clientX : e.clientY) -
      (horizontal ? rect.left : rect.top) -
      thumbSize / 2;
    const next = (Math.min(m.travel, Math.max(0, pos)) / m.travel) * m.overflow;
    if (horizontal) m.scroller.scrollLeft = next;
    else m.scroller.scrollTop = next;
  };

  return (
    <div
      ref={trackRef}
      hidden
      aria-hidden
      data-orientation={orientation}
      onPointerDown={onTrackPointerDown}
      // 12px tutma alanı; görünür thumb ortada 6px, hover'da 8px
      className={
        horizontal
          ? "gradient-scrollbar-track group/scrollbar absolute inset-x-1 bottom-0 z-20 flex h-3 cursor-pointer items-center"
          : "gradient-scrollbar-track group/scrollbar absolute inset-y-2 end-0 z-20 flex w-3 cursor-pointer justify-center"
      }
    >
      <div
        ref={thumbRef}
        onPointerDown={onThumbPointerDown}
        onPointerMove={onThumbPointerMove}
        onPointerUp={onThumbPointerUp}
        onPointerCancel={onThumbPointerUp}
        className={
          horizontal
            ? "gradient-scrollbar-thumb h-1.5 cursor-grab touch-none rounded-full transition-[height] duration-150 will-change-transform group-hover/scrollbar:h-2 active:cursor-grabbing"
            : "gradient-scrollbar-thumb w-1.5 cursor-grab touch-none rounded-full transition-[width] duration-150 will-change-transform group-hover/scrollbar:w-2 active:cursor-grabbing"
        }
      />
    </div>
  );
}
